import PropTypes from 'prop-types';
import React from 'react';
import CommonLayout from '../common/layouts/commonLayout';
import AudioContainer from '../common/audio/audioContainer';
import SecondsTimer from '../common/components/SecondsTimer';
import CircleButton from '../common/components/CircleButton';
import GoButton from '../common/components/GoButton';
import MessagePopup from '../common/components/MessagePopup';
import TimedView from '../common/timer/TimedView';
import { ARROW_DOWN, ARROW_UP, ENTER, SPACE } from '../constants/keyboardCodes';
import { ACTIVITY_TIMER_MESSAGE, ARIA_LABELS } from '../constants/text';
import { TEN_MINUTES_IN_SECONDS, TWO_SECONDS } from '../constants/time';
import ScreenErrorContainer from '../common/error/ScreenErrorContainer';
import { RESULT_RESPONSE_OUTCOME } from '../constants/measure';
import { RAN_ACTIVITY_TIMER } from './ranConstants';

export default class RANItemsView extends TimedView {
    constructor(props) {
        super(props);

        this.state = {
            isTimeUp: false,
        };
    }

    componentDidMount() {
        if (!this.props.isDataFetched) {
            this.props.fetchContent();
        } else {
            this.props.narrateScreen();
        }

        document.addEventListener('keydown', this.handleKeyDown);

        if (this.props.hasActivityTimer) {
            this.scheduleEvent(
                RAN_ACTIVITY_TIMER,
                () => {
                    this.setState({ isTimeUp: true });
                },
                TEN_MINUTES_IN_SECONDS,
            );
        }
    }

    componentDidUpdate(prevProps) {
        if (this.props.isDataFetched && !prevProps.isDataFetched) {
            this.props.narrateScreen();
        }
    }

    componentWillUnmount() {
        this.props.cleanup();
        document.removeEventListener('keydown', this.handleKeyDown);
    }

    getActiveItemBox(id) {
        const idArray = id.split('_');
        if (idArray[idArray.length - 1] === `item${this.props.activeItemIndex}`) {
            return ' active';
        }
        return '';
    }

    getItemBoxes() {
        return this.props.items.map((item, index) => {
            const itemId = item.itemId ? item.itemId : `practice_item${index}`;
            return (
                <div key={itemId} className={`item-box${this.getActiveItemBox(itemId)}`}>
                    {item.stimulus.text}
                </div>
            );
        }, this);
    }

    handleOnNextItem(selectedResponse) {
        return () => {
            let timeDuration;

            if (
                this.props.hasActivityTimer &&
                this.props.activeItemIndex === this.props.items.length - 1
            ) {
                const timer = this.getTimer(RAN_ACTIVITY_TIMER);
                timeDuration = timer.getTime();
            }

            return this.props.onNextItem(selectedResponse, timeDuration);
        };
    }

    handleOnNext = () => {
        this.resetTimer(RAN_ACTIVITY_TIMER);
        return this.props.onNext();
    };

    handleKeyDown = event => {
        // preventing onNext for practice screen
        // when screen is blocked
        if (this.props.isScreenBlocked) {
            return false;
        }

        const key = event.which || event.keyCode;

        if (key === ENTER || key === SPACE) {
            return event.preventDefault();
        }

        if (key === ARROW_UP) {
            return this.handleOnNextItem(RESULT_RESPONSE_OUTCOME.CORRECT)();
        }

        if (key === ARROW_DOWN) {
            return this.handleOnNextItem(RESULT_RESPONSE_OUTCOME.NOT_CORRECT)();
        }

        return true;
    };

    render() {
        return (
            <CommonLayout
                class={this.props.pageLayout}
                isScreenBlocked={this.state.isTimeUp || this.props.isScreenBlocked}
                isGreyedOut={this.state.isTimeUp || this.props.isScreenGreyedOut}
            >
                <ScreenErrorContainer onRetry={this.props.onNext} />
                <AudioContainer />
                <div className="ran-container">
                    {this.props.isSecondsTimerVisible && (
                        <SecondsTimer
                            isTimerActive={this.props.isSecondsTimerActive}
                            activateTimer={this.props.activateSecondsTimer}
                        />
                    )}
                    <div className="item-box-container">{this.getItemBoxes()}</div>
                    {this.props.isCircleButtonsVisible && (
                        <div className="ran-answer-buttons">
                            <CircleButton
                                isDisabled={this.props.isGreyedOut || this.props.isScreenBlocked}
                                ariaLabel="Correct"
                                class="circle-button-left"
                                onClick={this.handleOnNextItem(RESULT_RESPONSE_OUTCOME.CORRECT)}
                            />
                            <CircleButton
                                isDisabled={this.props.isGreyedOut || this.props.isScreenBlocked}
                                ariaLabel="Incorrect"
                                class="circle-button-right"
                                onClick={this.handleOnNextItem(RESULT_RESPONSE_OUTCOME.NOT_CORRECT)}
                            />
                        </div>
                    )}
                </div>

                {this.state.isTimeUp && (
                    <MessagePopup
                        delayedCallback={this.props.goToStartPage}
                        delay={TWO_SECONDS}
                        message={ACTIVITY_TIMER_MESSAGE}
                    />
                )}

                {this.props.isNextButtonActive && (
                    <GoButton onClick={this.handleOnNext} ariaLabel={ARIA_LABELS.GO_TO_NEXT_STEP} />
                )}
            </CommonLayout>
        );
    }
}

RANItemsView.propTypes = {
    activeItemIndex: PropTypes.number,
    responseTimeStart: PropTypes.number,
    pageLayout: PropTypes.string,
    isDataFetched: PropTypes.bool,
    isScreenBlocked: PropTypes.bool,
    isScreenGreyedOut: PropTypes.bool,
    isCircleButtonsVisible: PropTypes.bool,
    isNextButtonActive: PropTypes.bool,
    isSecondsTimerVisible: PropTypes.bool,
    isSecondsTimerActive: PropTypes.bool,
    hasActivityTimer: PropTypes.bool,
    items: PropTypes.array,
    onNextItem: PropTypes.func,
    narrateScreen: PropTypes.func,
    onNext: PropTypes.func,
    activateSecondsTimer: PropTypes.func,
    fetchContent: PropTypes.func,
    cleanup: PropTypes.func,
    goToStartPage: PropTypes.func,
};

RANItemsView.defaultProps = {
    activeItemIndex: undefined,
    responseTimeStart: undefined,
    pageLayout: '',
    isDataFetched: false,
    isScreenBlocked: false,
    isScreenGreyedOut: false,
    isCircleButtonsVisible: false,
    isNextButtonActive: false,
    isSecondsTimerVisible: false,
    isSecondsTimerActive: true,
    hasActivityTimer: false,
    items: [],
    onNextItem: () => {},
    narrateScreen: () => {},
    onNext: () => {},
    activateSecondsTimer: () => {},
    fetchContent: () => {},
    cleanup: () => {},
    goToStartPage: () => {},
};
